import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit, FileText, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const Row = ({ label, value }) => (
  <div>
    <div className="text-xs text-slate-500">{label}</div>
    <div className="text-sm text-slate-900 font-medium mt-0.5">{value || '—'}</div>
  </div>
);

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    api.get(`/clients/${id}`).then(r => { setData(r.data); setForm(r.data.client); });
  }, [id]);

  const save = async () => {
    await api.put(`/clients/${id}`, form);
    toast.success("Alterações salvas");
    setEditing(false);
    const r = await api.get(`/clients/${id}`);
    setData(r.data);
  };

  if (!data) return <div className="p-8 text-slate-500">Carregando...</div>;
  const c = editing ? form : data.client;

  return (
    <div className="flex-1 min-w-0 flex flex-col p-6 lg:p-8 font-sans max-w-5xl mx-auto w-full">
      <button onClick={() => navigate('/clients')} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-4" data-testid="btn-back">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>

      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">{c.nome_completo || 'Cliente'}</h1>
          <p className="text-slate-500 mt-1 text-sm">Cadastrado em {new Date(c.created_at).toLocaleString('pt-BR')}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditing(!editing)} data-testid="btn-edit" className="px-4 h-10 rounded-lg border border-slate-200 text-sm inline-flex items-center gap-2 hover:bg-slate-50">
            <Edit className="w-4 h-4" /> {editing ? 'Cancelar' : 'Editar'}
          </button>
          {editing ? (
            <button onClick={save} data-testid="btn-save-client" className="gradient-primary text-white px-4 h-10 rounded-lg text-sm inline-flex items-center gap-2 hover:-translate-y-0.5 transition">
              <Save className="w-4 h-4"/> Salvar
            </button>
          ) : (
            <Link to={`/clients/${id}/new-document`} data-testid="btn-new-doc" className="gradient-primary text-white px-4 h-10 rounded-lg text-sm inline-flex items-center gap-2 hover:-translate-y-0.5 transition">
              <FileText className="w-4 h-4"/> Gerar documento
            </Link>
          )}
        </div>
      </div>

      {editing ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-6 rounded-2xl border border-slate-100">
          {[
            ["nome_completo","Nome completo"],["cpf","CPF"],["rg","RG"],["rg_orgao","Órgão"],["rg_uf","UF RG"],
            ["cnh","CNH"],["data_nascimento","Nascimento (DD/MM/AAAA)"],["nome_mae","Mãe"],["nome_pai","Pai"],
            ["telefone","Telefone"],["whatsapp","WhatsApp"],["email","E-mail"],
            ["endereco_logradouro","Logradouro"],["endereco_numero","Número"],["endereco_complemento","Complemento"],
            ["endereco_bairro","Bairro"],["endereco_cidade","Cidade"],["endereco_uf","UF"],["endereco_cep","CEP"],
          ].map(([k,l]) => (
            <label key={k} className="block">
              <div className="text-xs font-medium text-slate-600 mb-1">{l}</div>
              <input value={form[k] || ''} onChange={(e) => setForm({...form, [k]: e.target.value})}
                className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-slate-900 outline-none text-sm"/>
            </label>
          ))}
          <label className="block">
            <div className="text-xs font-medium text-slate-600 mb-1">Área do direito</div>
            <select value={form.area || 'outros'} onChange={(e) => setForm({...form, area: e.target.value})} className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm">
              <option value="civil">Direito Civil</option>
              <option value="tributario">Direito Tributário</option>
              <option value="previdenciario">Direito Previdenciário</option>
              <option value="penal">Direito Penal</option>
              <option value="familia">Direito de Família</option>
              <option value="outros">Outros</option>
            </select>
          </label>
          <label className="block">
            <div className="text-xs font-medium text-slate-600 mb-1">Status</div>
            <select value={form.status || 'nao_concluido'} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm">
              <option value="nao_concluido">Não concluído</option>
              <option value="concluido">Concluído</option>
            </select>
          </label>
        </div>
      ) : (
        <>
          <section className="bg-white rounded-2xl border border-slate-100 p-6 mb-6">
            <h2 className="font-heading text-lg font-semibold mb-4">Dados pessoais</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Row label="CPF" value={c.cpf}/><Row label="RG" value={c.rg}/><Row label="CNH" value={c.cnh}/>
              <Row label="Nascimento" value={c.data_nascimento}/><Row label="Naturalidade" value={c.naturalidade}/>
              <Row label="Nacionalidade" value={c.nacionalidade}/>
              <Row label="Mãe" value={c.nome_mae}/><Row label="Pai" value={c.nome_pai}/>
              <Row label="Telefone" value={c.telefone}/><Row label="E-mail" value={c.email}/>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-slate-100 p-6 mb-6">
            <h2 className="font-heading text-lg font-semibold mb-4">Endereço</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Row label="Logradouro" value={c.endereco_logradouro}/>
              <Row label="Número" value={c.endereco_numero}/>
              <Row label="Complemento" value={c.endereco_complemento}/>
              <Row label="Bairro" value={c.endereco_bairro}/>
              <Row label="Cidade" value={c.endereco_cidade}/>
              <Row label="UF" value={c.endereco_uf}/>
              <Row label="CEP" value={c.endereco_cep}/>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-slate-100 p-6">
            <h2 className="font-heading text-lg font-semibold mb-4">Documentos gerados</h2>
            {data.documents.length === 0 ? (
              <div className="text-sm text-slate-500">Nenhum documento gerado ainda.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.documents.map(d => (
                  <li key={d.id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{d.title}</div>
                      <div className="text-xs text-slate-500">{new Date(d.created_at).toLocaleString('pt-BR')}</div>
                    </div>
                    <Link to={`/documents/${d.id}`} className="text-sm text-[#0f4c5c] hover:underline">Abrir</Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
